import {
  Data,
  Query,
  Variables,
  Mutation as IMutation,
} from '__generated__/graphql';
import useInterval from 'hooks/useInterval';
import { useShallowEffect } from 'hooks/useShallowEffect';
import { Provided } from 'queries/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { afterWebVitals } from 'utils/pageLoad';
import { useClientContext } from '$/modules/context/client';
import {
  ClientOperationResponse,
  fetchClientOperation,
} from '$/modules/graphql-client/fetchClientOperation';

type CommittedState = {
  variables: Record<string, unknown>;
  data: unknown;
};

type ExternalState = {
  initial: boolean;
  loading?: number;
  controller?: AbortController;
};

export type QueryResult<R, V> = {
  // Query results
  data: R;
  // Variables that where used to query the current results (possibly stale)
  variables: V;
  // True when new data is being fetched
  loading: boolean;
};

export type QueryOptions<R> = {
  timeout?: number;
  initialData?: R;
  enabled?: boolean;
  refetchInterval?: number | false | ((data: R) => number | false);
};

/**
 * Executes a GraphQL query
 */
export function useQuery<O extends Query>(
  query: O,
  variables: Provided<Variables<O>>,
  options?: QueryOptions<Data<O>>,
): QueryResult<Data<O>, Variables<O>> {
  const { lang } = useClientContext();

  const injected = (
    query.inject ? { language: lang, ...(variables ?? {}) } : variables
  ) as Variables<O>;

  const ref = useRef<ExternalState>({
    initial: true,
  });

  const [results, setResults] = useState<CommittedState>({
    variables: injected as Record<string, unknown>,
    data: options?.initialData,
  });

  const [loading, setLoading] = useState<boolean>(
    !options?.initialData && options?.enabled !== false,
  );

  const [timer, setTimer] = useState(0);
  const interval =
    typeof options?.refetchInterval === 'function'
      ? options.refetchInterval(results.data as Data<O>)
      : (options?.refetchInterval ?? false);
  useInterval(() => setTimer(Date.now()), interval);

  // Update state when props change
  useShallowEffect(
    () => {
      if (ref.current.initial && options?.initialData) {
        ref.current.initial = false;
      } else {
        ref.current.initial = false;

        // never fetch when disabled
        if (options?.enabled === false) return;

        const current = ref.current;
        const finishedLoading = () => {
          if (current.loading) {
            clearTimeout(current.loading);
            current.loading = undefined;
          }
          setLoading(false);
        };

        afterWebVitals
          .then(async () => {
            if (!current.loading) {
              current.loading = setTimeout(
                () => setLoading(true),
                options?.timeout ?? 300,
              ) as unknown as number;
            }
            if (current.controller) {
              current.controller.abort();
              current.controller = undefined;
            }
            current.controller = new AbortController();
            const operation = fetchClientOperation(query, injected, {
              signal: current.controller?.signal,
              acceptedLanguage: lang,
            });
            for await (const response of operation) {
              setResults({
                variables: variables as Record<string, unknown>,
                data: response.data,
              });
              finishedLoading();
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            finishedLoading();
          });
      }
    },
    {
      _query: query,
      _enabled: options?.enabled,
      _timer: timer,
      ...(injected ?? {}),
    },
  );

  return {
    loading,
    data: results.data as Data<O>,
    variables: results.variables as Variables<O>,
  };
}

export interface Mutation<T, V> {
  send(variables: Provided<V>): Promise<ClientOperationResponse<T>>;
}

export function useMutation<O extends IMutation>(
  operation: O,
): Mutation<Data<O>, Variables<O>> {
  const { lang } = useClientContext();
  return useMemo(() => {
    return {
      async send(variables) {
        if (operation.inject) {
          variables = { language: lang, ...variables };
        }
        const data = await fetchClientOperation(
          operation,
          variables as Variables<O>,
          { acceptedLanguage: lang },
        ).next();
        return data.value;
      },
    };
  }, [operation, lang]);
}

export type QueryState<V> = {
  setState(f: ((state: V) => V) | V): void;
  // Retrieve the variables that are pending
  useState(): V;
  state: V;
};

export function useQueryState<V extends Record<string, unknown>>(
  initialState: V,
): QueryState<V> {
  const [state, setState] = useState<V>(initialState);
  const ref = useRef<V>(initialState);
  ref.current = state;

  const setInternalState = useCallback((f: ((state: V) => V) | V) => {
    const next = typeof f === 'function' ? f(ref.current) : f;
    // temporary fix for incorrect variable type
    if ('repaymentPeriodM' in next && next.repaymentPeriodM != null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is generic
      (next as any).repaymentPeriodM = Number(next.repaymentPeriodM);
    }
    setState(next);
  }, []);

  const useInternalState = useCallback(() => {
    return ref.current;
  }, []);

  return { setState: setInternalState, useState: useInternalState, state };
}
